import { gallery, h, img, p } from '@/content/blocks'
import type { Project } from '@/types/content'

const project: Project = {
  slug: 'sterilization-container',
  title: 'Sterilization Container',
  summary: 'Sterilization for medical tools.',
  cover: { src: '/assets/P2_Container.png', alt: 'Render of the sterilization container' },
  tags: ['CAD', 'Design'],
  order: 6,
  blocks: [
    img(
      '/assets/P2-Final-Render.gif',
      'Rendered animation of the finished sterilization container turning to show its patterned lid and lightening holes',
    ),

    h('Overview'),
    p(
      'We were challenged as a team to design a system that would transfer surgical tools using a robotic arm to an autoclave bin for sterilization. To accomplish this, we were split into a design subteam and programming subteam. I worked on the design of this system and through the engineering design process created a container that would meet our objectives and constraints. We defined objectives to allow a secure hold of a clamp (the medical tool assigned to us) in place, allow for sterilization, and will be transported by a robotic arm. The design was constrained through the size and shape of our footprint, a mass of under 350g, and maintaining features greater than 2 mm. Our design implements a lofted mould of the clamp that will securely hold the tool as it falls in place laterally and a slide-able lid that would account for any form of displacement—specifically in the event the container was flipped—during transportation. The design conforms to the size and shape of the footprint as a scaled-down version of the geometry allowing for additional space translationally. Furthermore, to facilitate sterilization, lightening holes were created around the container and lid while maintaining structural rigidity by complying with feature constraints and structurally sound patterning.',
    ),

    h('Prototyping'),
    p(
      'Generally, when you are introduced to a problem oftentimes there won\'t be any inherent solution that will come up. Our design process worked quite similarly, as we had to first define our objectives and determine means to achieve these objectives. Having said that we worked on creating a morph chart so that we would have some indication of how we should approach our design.',
    ),
    img(
      '/assets/P2-MorphChart.png',
      'Morph chart with rows for picking the container up, facilitating sterilization, and holding the tool, each listing six candidate means',
    ),
    p(
      'From there were experimented with potential designs with preliminary sketches and prototyped to ensure the design would work from a practical standpoint.',
    ),
    img(
      '/assets/P2-DraftSketch.jpg',
      'Hand sketch of a rectangular container holding a pair of scissors, with triangular cut-outs in the lid and cross-hatching across the front face',
      { maxWidth: '800px' },
    ),
    gallery([
      {
        src: '/assets/P2-LowFidelityProto.png',
        alt: 'Cardboard low-fidelity prototype of the container taped together on a desk, with wooden sticks forming a handle on one side',
      },
      {
        src: '/assets/P2-LowFidelityProto2.png',
        alt: 'Top view of the cardboard prototype with a pair of scissors standing in for the clamp, held in place by small orange stops',
        maxWidth: '800px',
      },
    ]),

    h('CAD Evolution'),
    p(
      'From then on, most of the work was done over CAD. This was one of my favourite parts of the project because implementing CAD gives a strong sense of how your design will come to fruition and reveals what parts will be able to work or where the weakness in your design exists.',
    ),

    h('First CAD Iteration', 4),
    p(
      'It was pretty clear that the first iteration of my CAD model was not very functional and practical in terms of the problem we were trying to solve. Although, this was a fairly quick and non-technical mock-up of what we wanted the design to be, it helped highlight several issues with our approach. The handle was designed too fragile and weak, we also didn\'t know if the claw would be able to fit through the handles (also given that it would be difficult to extend it out anymore when we were constrained by our footprint). Knowing that one of the weakest parts of this model was going to be one of our primary objectives, we quickly veto\'ed the idea of using handles. The latches also did not make much sense from a practical standpoint because the objective the design is to automate sterilization so that surgeons may focus on monitoring more important things.',
    ),
    gallery([
      {
        src: '/assets/P2-FirstCAD.png',
        alt: 'First CAD iteration of the container, a boxy shell with a triangular lattice lid and a thin handle bar spanning the front face',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-FirstCAD2.png',
        alt: 'Front elevation of the first CAD iteration, showing rows of triangular lightening holes and the handle bar across the middle',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-FirstCAD3.png',
        alt: 'First CAD iteration seen from the opposite corner, with the lattice lid and the handle on the long side',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-FirstCAD4.png',
        alt: 'Top view of the first CAD iteration with the clamp seated inside and handles protruding from both sides of the container',
        maxWidth: '300px',
      },
    ]),

    h('Second CAD Iteration', 4),
    p(
      'We had to rethink our approach and one of the key highlights and values of the problem. So, we decided that the best way to avoid any non-automated work was by creating a mould that would help funnel the tool to the bottom just by dropping the tool into the container. As for the how it would be picked up, we decided by adding cross hatches to the sides of the container would be sufficient as it would provide friction for the claw to grab. This time, I gave myself more time to design and think about the outcome of the container so the CAD model was much more polished for fabrication. At this point, we were fairly ahead so we waited for design review and considered the modes of failure of our design. Aside from tolerance concerns with 3D-printing, one consideration we had was if the arm were to flip over during transportation which would cause the tool to fall.',
    ),
    gallery([
      {
        src: '/assets/P2-SecondCAD.png',
        alt: 'Second CAD iteration opened up, showing the walls lofted inward to funnel the clamp to the bottom, with a recessed cross-hatch panel on one face',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-SecondCAD2.png',
        alt: 'Second CAD iteration from the opposite side, with slotted lightening holes on one wall and the triangular cross hatches that give the claw friction on the other',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-SecondCAD3.png',
        alt: 'Second CAD iteration viewed along the narrow handle channel, which ends in a column of four round holes',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-SecondCAD4.png',
        alt: 'Top view of the second CAD iteration, the cavity lofted to the outline of the clamp with slots at the jaws and graduated round holes down the handle',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-SecondCAD5.png',
        alt: 'Underside of the second CAD iteration, showing the graduated round holes along the handle and the slotted vents in the base',
        maxWidth: '600px',
      },
    ]),

    h('Final Product'),
    p(
      'After realizing there was a mode of failure in our design, we addressed it by adding a lid to the container that would slide in and out fairly easily without falling out on its own either. That made it so that the tool would be indefinitely secure during transportation. From there, we got the container 3D printed as a mockup of how it turned out.',
    ),
    gallery([
      {
        src: '/assets/P2-Container-no-Lid.png',
        alt: 'Top view of the final container without its lid, the cavity lofted to the shape of the clamp with slots at the jaws and graduated holes along the handle',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-Container-w-Lid.png',
        alt: 'Final container with the patterned sliding lid in place and a 4 embossed on the side wall',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-Container-W-Footprint.png',
        alt: 'Final container with its lid seated inside the allotted footprint it had to conform to',
        maxWidth: '600px',
      },
    ]),
    gallery([
      {
        src: '/assets/P2-3DPrinted.jpg',
        alt: '3D-printed container with the patterned lid slid into place, resting on a wooden desk',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-3DPrinted2.jpg',
        alt: '3D-printed container with the lid removed, showing the moulded cavity that funnels the clamp to the bottom',
        maxWidth: '600px',
      },
      {
        src: '/assets/P2-3DPrinted3.jpg',
        alt: 'Top view of the 3D-printed container with its lid on, the lattice pattern and the graduated holes down the handle channel visible',
        maxWidth: '400px',
      },
    ]),
  ],
}

export default project
